import { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  apiLoadErrorText,
  useListFleetModelsQuery,
  useListPrcCurrentQuery,
  useListPrcVersionsQuery,
  type PrcModelPrice,
} from '@shared/api';
import { useActiveProjectContext } from '@shared/lib/activeProjectContext';
import { Button } from '@shared/ui/Button';
import { SkeletonLine } from '@shared/ui/Skeleton';
import { IconPlus, IconUpload } from '@shared/ui/icons';
import { fleetLeading, fleetType, isRecord, typo } from '@entities/fleet';
import { PRC_COMMON_LABEL, formatIsoDate, groupVersionsByObject } from '@entities/prc';
import { Notice, TextButton } from '@widgets/FleetRegistry/ui';
import { usePrcAccess } from '../access';
import { MODELS, MODELS_GEN, VERSIONS, count } from '../format';
import { EmptyBox, NARROW, PrcRoot } from '../ui';
import { ImportDrawer } from './ImportDrawer';
import { NewVersionDrawer } from './NewVersionDrawer';
import { VersionsTable } from './VersionsTable';

const Bar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(4)}`};
  margin: ${({ theme }) => `${theme.spacing(5)} 0 ${theme.spacing(3)}`};

  & > .count {
    margin: 0;
    font-size: ${fleetType.body};
    color: ${({ theme }) => theme.colors.text.secondary};
    font-variant-numeric: tabular-nums;
  }

  & > .count > strong {
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: 600;
  }

  & > .actions {
    display: flex;
    flex-wrap: wrap;
    gap: ${({ theme }) => theme.spacing(2)};
    margin-left: auto;
  }

  @container prc (max-width: ${NARROW}) {
    & > .actions {
      flex: 1 1 100%;
      margin-left: 0;
    }

    & > .actions > button {
      flex: 1 1 200px;
    }
  }
`;

const FlashRow = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const NoticeRow = styled.div`
  margin-top: ${({ theme }) => theme.spacing(4)};
`;

/** Чьи версии на экране: объект из шапки или все объекты. */
const Scope = styled.p`
  margin: ${({ theme }) => `0 0 ${theme.spacing(4)}`};
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.normal};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-wrap: pretty;

  & > strong {
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: 600;
  }
`;

/** Раздел списка — объект и его версии, когда в шапке «все объекты». */
const Section = styled.section`
  & + & {
    margin-top: ${({ theme }) => theme.spacing(6)};
  }

  & > h2 {
    margin: ${({ theme }) => `0 0 ${theme.spacing(2)}`};
    font-size: ${fleetType.base};
    font-weight: 600;
    line-height: ${fleetLeading.tight};
    overflow-wrap: anywhere;
  }
`;

/** Действующие ПРЦ — как штамп на листе: что сейчас записано в справочник техники. */
const Current = styled.section`
  display: grid;
  gap: ${({ theme }) => theme.spacing(1)};
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(4)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-left: 3px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};

  & > h2 {
    margin: 0;
    font-size: ${fleetType.micro};
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  & > p,
  & > ul {
    margin: 0;
    font-size: ${fleetType.base};
    line-height: ${fleetLeading.normal};
    text-wrap: pretty;
  }

  & > ul {
    padding: 0;
    list-style: none;
  }

  & .muted {
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

interface CurrentGroup {
  versionId: string;
  /** Вариант условий цены; `null` — основной расчёт версии. */
  variantId: string | null;
  label: string;
  validFrom: string;
  /** Чья цена: объект; `null` — общая. */
  object: string | null;
  models: number;
}

function groupCurrent(items: readonly PrcModelPrice[]): CurrentGroup[] {
  const map = new Map<string, CurrentGroup>();
  for (const p of items) {
    const key = `${p.version_id}|${p.valid_from}|${p.project_id ?? ''}`;
    const g = map.get(key);
    if (g) g.models += 1;
    else {
      map.set(key, {
        versionId: p.version_id,
        variantId: p.variant_id ?? null,
        label: p.variant_name
          ? `${p.version_label ?? 'версия удалена'} — вариант «${p.variant_name}»`
          : (p.version_label ?? 'версия удалена'),
        validFrom: p.valid_from,
        object: p.project_name,
        models: 1,
      });
    }
  }
  return Array.from(map.values()).sort((a, b) => b.models - a.models);
}

/** Ссылка на версию цены — в условиях её варианта, если цена по варианту. */
function versionLink(g: CurrentGroup): string {
  const base = `/agents/econ/prc/${g.versionId}`;
  return g.variantId ? `${base}?cond=${g.variantId}` : base;
}

/** «для объекта «…»» / «общая для всех объектов» — чья действующая цена. */
function objectNote(object: string | null): string {
  return object ? `для объекта «${object}»` : PRC_COMMON_LABEL.toLowerCase();
}

function CurrentPrices({ projectId }: { projectId: string }) {
  const { data, isLoading, isError } = useListPrcCurrentQuery(
    projectId ? { project_id: projectId } : undefined,
  );
  const groups = useMemo(() => groupCurrent(data?.items ?? []), [data]);
  if (isLoading || isError) return null;
  // Моделей, а не строк: у версии на несколько объектов и вариантов цена
  // модели записана на каждый объект.
  const total = new Set((data?.items ?? []).map((p) => p.model_id)).size;
  return (
    <Current aria-labelledby="prc-current-h">
      <h2 id="prc-current-h">Действует в справочнике техники</h2>
      {total === 0 ? (
        <p className="muted">
          {typo(
            projectId
              ? 'Для этого объекта утверждённой ПРЦ нет — ни своей, ни общей. Цена машино-часа появится, когда утвердят версию этого объекта или общую.'
              : 'Утверждённых ПРЦ пока нет: цена машино-часа попадёт в справочник техники, когда версию утвердят.',
          )}
        </p>
      ) : groups.length === 1 && groups[0] ? (
        <p>
          {typo(`ПРЦ ${count(total, MODELS_GEN)} по версии`)}{' '}
          <Link to={versionLink(groups[0])}>«{groups[0].label}»</Link> с{' '}
          {formatIsoDate(groups[0].validFrom)}
          <span className="muted"> — {objectNote(groups[0].object)}</span>
        </p>
      ) : (
        <>
          <p>{typo(`ПРЦ ${count(total, MODELS_GEN)}:`)}</p>
          <ul>
            {groups.map((g) => (
              <li key={`${g.versionId}-${g.validFrom}-${g.object ?? ''}`}>
                <Link to={versionLink(g)}>«{g.label}»</Link>
                <span className="muted">
                  {' '}
                  — {count(g.models, MODELS)} с {formatIsoDate(g.validFrom)}, {objectNote(g.object)}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </Current>
  );
}

function ListSkeleton() {
  return (
    <div aria-busy="true" aria-label="Загрузка версий ПРЦ">
      {Array.from({ length: 6 }, (_, i) => (
        <SkeletonLine key={i} $height={20} $width={i % 3 === 0 ? '45%' : '100%'} />
      ))}
    </div>
  );
}

/**
 * Раздел «ПРЦ машино-часа»: версии расчёта, действующие цены, новый расчёт и
 * загрузка книги ЭММ. Карточка версии — `/agents/econ/prc/:id`.
 */
export function PrcVersionsPage() {
  const access = usePrcAccess();
  const navigate = useNavigate();
  const location = useLocation();
  // Сообщение после удаления черновика: карточка версии передаёт его в state перехода.
  const [flash, setFlash] = useState<string | null>(() =>
    isRecord(location.state) && typeof location.state.prcFlash === 'string'
      ? location.state.prcFlash
      : null,
  );
  const [dialog, setDialog] = useState<'new' | 'import' | null>(null);
  // Объект из шапки ОСНОВЫ: его версии и общие; без объекта — все, по объектам.
  const { activeProjectId, activeProjectName } = useActiveProjectContext();
  // Список перечитывается при каждом открытии: копию мог сделать другой человек.
  const { data, isLoading, isError, error, refetch, isFetching } = useListPrcVersionsQuery(
    activeProjectId ? { project_id: activeProjectId } : undefined,
    { refetchOnMountOrArgChange: true },
  );
  const { data: fleet } = useListFleetModelsQuery({}, { skip: !access.canWrite });
  const items = data?.items ?? [];
  const sections = useMemo(
    () => (activeProjectId ? [] : groupVersionsByObject(items)),
    [activeProjectId, items],
  );

  // Расчёт берёт модели «в работе» и «в резерве» справочника техники.
  const registryModels = (fleet?.items ?? []).filter(
    (m) => m.status === 'active' || m.status === 'reserve',
  ).length;
  const registryEmpty = access.canWrite && fleet !== undefined && registryModels === 0;
  const open = (id: string) => navigate(`/agents/econ/prc/${id}`);

  const buttons = access.canWrite && (
    <>
      <Button
        type="button"
        $variant="secondary"
        disabled={registryEmpty}
        title={registryEmpty ? 'Сначала загрузите паспорт техники в «Технике»' : undefined}
        onClick={() => setDialog('import')}
      >
        <IconUpload size={16} />
        Загрузить книгу ЭММ
      </Button>
      <Button
        type="button"
        $variant="primary"
        disabled={registryEmpty}
        title={registryEmpty ? 'Сначала загрузите паспорт техники в «Технике»' : undefined}
        onClick={() => setDialog('new')}
      >
        <IconPlus size={16} />
        Новый расчёт
      </Button>
    </>
  );

  return (
    <PrcRoot>
      {flash && (
        <FlashRow>
          <Notice $tone="success" role="status">
            <span className="text">{flash}</span>
            <TextButton
              type="button"
              onClick={() => {
                setFlash(null);
                navigate({ search: location.search }, { replace: true, state: null });
              }}
            >
              Скрыть
            </TextButton>
          </Notice>
        </FlashRow>
      )}

      <Scope>
        {activeProjectId ? (
          <>
            {typo('Объект в шапке:')} <strong>«{activeProjectName || 'выбранный объект'}»</strong>
            {typo(
              ' — здесь его расчёты ПРЦ и общие. Другой объект или «все объекты» — в шапке ОСНОВЫ.',
            )}
          </>
        ) : (
          typo(
            'Все объекты: расчёты сгруппированы по объектам. Выберите объект в шапке ОСНОВЫ — останутся его расчёты и общие.',
          )
        )}
      </Scope>

      <CurrentPrices projectId={activeProjectId} />

      {registryEmpty && (
        <NoticeRow>
          <Notice $tone="warning">
            <span className="text">
              <strong>Справочник техники пуст.</strong>{' '}
              {typo(
                'ПРЦ считается по паспортам моделей «в работе» и «в резерве» — сначала загрузите паспорт техники из книги ЭММ.',
              )}
            </span>
            <Link to="/agents/econ/fleet">Открыть «Технику»</Link>
          </Notice>
        </NoticeRow>
      )}

      {isError ? (
        <Bar>
          <Notice $tone="danger" role="alert">
            <span className="text">
              {apiLoadErrorText(
                error,
                'Не удалось загрузить версии ПРЦ.',
                'Нет доступа к ПРЦ Экономиста.',
              )}
            </span>
            <TextButton type="button" onClick={() => void refetch()}>
              Повторить
            </TextButton>
          </Notice>
        </Bar>
      ) : isLoading ? (
        <Bar>
          <ListSkeleton />
        </Bar>
      ) : items.length === 0 ? (
        <EmptyBox>
          <h2>{activeProjectId ? 'Для этого объекта расчётов ПРЦ нет' : 'Расчётов ПРЦ ещё нет'}</h2>
          <p>
            {typo(
              access.canWrite
                ? 'Загрузите книгу «Калькуляция ЭММ» — ОСНОВА перенесёт цены, ФОТ механизаторов, параметры и лимиты на ремонт в черновик и сверит расчёт с Excel. Или создайте расчёт из книги, уже загруженной в справочник техники.'
                : 'Расчёты ПРЦ ведут экономисты: как только появится первая версия, она будет здесь.',
            )}
          </p>
          {buttons && <div className="actions">{buttons}</div>}
        </EmptyBox>
      ) : (
        <>
          <Bar>
            <p className="count" aria-live="polite">
              <strong>{count(items.length, VERSIONS)}</strong>
              {isFetching ? ' · обновляю…' : ''}
            </p>
            {buttons && <div className="actions">{buttons}</div>}
          </Bar>
          {activeProjectId ? (
            <VersionsTable items={items} />
          ) : (
            sections.map((sec) => (
              <Section key={sec.key} aria-labelledby={`prc-sec-${sec.key}`}>
                <h2 id={`prc-sec-${sec.key}`}>{sec.label}</h2>
                <VersionsTable items={sec.items} all={items} />
              </Section>
            ))
          )}
        </>
      )}

      {dialog === 'new' && (
        <NewVersionDrawer versions={items} onClose={() => setDialog(null)} onCreated={open} />
      )}
      {dialog === 'import' && (
        <ImportDrawer
          onClose={() => {
            setDialog(null);
            // Закрыли, пока сервер разбирал книгу: черновик всё равно появится.
            void refetch();
          }}
          onOpen={open}
        />
      )}
    </PrcRoot>
  );
}
